import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext'
import { useLexicalEditable } from '@lexical/react/useLexicalEditable'
import {
  $deleteTableColumn__EXPERIMENTAL,
  $deleteTableRow__EXPERIMENTAL,
  $getNodeTriplet,
  $getTableCellNodeFromLexicalNode,
  $getTableColumnIndexFromTableCellNode,
  $getTableNodeFromLexicalNodeOrThrow,
  $getTableRowIndexFromTableCellNode,
  $insertTableColumn__EXPERIMENTAL,
  $insertTableRow__EXPERIMENTAL,
  $isTableCellNode,
  $isTableRowNode,
  $isTableSelection,
  $unmergeCell,
  getTableObserverFromTableElement,
  TableCellHeaderStates,
  TableCellNode,
  TableRowNode,
} from '@lexical/table'
import type { TableSelection } from '@lexical/table'
import type { LexicalEditor } from 'lexical'
import {
  $createParagraphNode,
  $getRoot,
  $getSelection,
  $isElementNode,
  $isParagraphNode,
  $isRangeSelection,
  $isTextNode,
} from 'lexical'
import * as React from 'react'
import { useCallback, useEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'

import useModal from '../hooks/use-modal'
import ColorPicker from '../ui/color-picker'
import { ChevronDown } from 'lucide-react'
import invariant from '../plugins/table-plugin'

function computeSelectionCount(
  selection: TableSelection
): { columns: number; rows: number } {
  const selectionShape = selection.getShape()
  return {
    columns: selectionShape.toX - selectionShape.fromX + 1,
    rows: selectionShape.toY - selectionShape.fromY + 1,
  }
}

function isTableSelectionRectangular(selection: TableSelection): boolean {
  const nodes = selection.getNodes()
  const currentRows: number[] = []
  let currentRow: TableRowNode | null = null
  let expectedColumns: number | null = null
  let currentColumns = 0

  for (let i = 0; i < nodes.length; i++) {
    const node = nodes[i]
    if ($isTableCellNode(node)) {
      const row = node.getParentOrThrow()
      invariant($isTableRowNode(row), 'Expected CellNode to have a RowNode parent')

      if (currentRow !== row) {
        if (expectedColumns !== null && currentColumns !== expectedColumns) {
          return false
        }
        if (currentRow !== null) {
          expectedColumns = currentColumns
        }
        currentRow = row as TableRowNode
        currentColumns = 0
      }

      const colSpan = node.getColSpan()
      for (let j = 0; j < colSpan; j++) {
        if (currentRows[currentColumns + j] === undefined) {
          currentRows[currentColumns + j] = 0
        }
        currentRows[currentColumns + j] += node.getRowSpan()
      }
      currentColumns += colSpan
    }
  }

  return (
    (expectedColumns === null || currentColumns === expectedColumns) &&
    currentRows.every((v) => v === currentRows[0])
  )
}

function $canUnmerge(): boolean {
  const selection = $getSelection()
  if (
    ($isRangeSelection(selection) && !selection.isCollapsed()) ||
    ($isTableSelection(selection) && !selection.anchor.is(selection.focus)) ||
    (!$isRangeSelection(selection) && !$isTableSelection(selection))
  ) {
    return false
  }
  const [cell] = $getNodeTriplet(selection.anchor)
  if ($isTableCellNode(cell)) {
    return cell.getColSpan() > 1 || cell.getRowSpan() > 1
  }
  return false
}

function $cellContainsEmptyParagraph(cell: TableCellNode): boolean {
  if (cell.getChildrenSize() !== 1) return false
  const firstChild = cell.getFirstChildOrThrow()
  if (!$isParagraphNode(firstChild) || !firstChild.isEmpty()) return false
  return true
}

function $selectLastDescendant(node: TableCellNode): void {
  const lastDescendant = node.getLastDescendant()
  if ($isTextNode(lastDescendant)) {
    lastDescendant.select()
  } else if ($isElementNode(lastDescendant)) {
    lastDescendant.selectEnd()
  } else if (lastDescendant !== null) {
    lastDescendant.selectNext()
  }
}

function currentCellBackgroundColor(editor: LexicalEditor): string | null {
  return editor.getEditorState().read(() => {
    const selection = $getSelection()
    if ($isRangeSelection(selection) || $isTableSelection(selection)) {
      const [cell] = $getNodeTriplet(selection.anchor)
      if ($isTableCellNode(cell)) {
        return cell.getBackgroundColor()
      }
    }
    return null
  })
}

function TableActionMenu({
  onClose,
  tableCellNode: initialTableCellNode,
  setIsMenuOpen,
  contextRef,
  cellMerge,
  showColorPickerModal,
}: {
  onClose: () => void
  tableCellNode: TableCellNode
  setIsMenuOpen: (isOpen: boolean) => void
  contextRef: React.RefObject<HTMLElement | null>
  cellMerge: boolean
  showColorPickerModal: (
    title: string,
    getContent: (onClose: () => void) => React.ReactNode
  ) => void
}): React.JSX.Element {
  const [editor] = useLexicalComposerContext()
  const dropDownRef = useRef<HTMLDivElement>(null)

  const [tableCellNode, setTableCellNode] = useState<TableCellNode>(
    initialTableCellNode
  )
  const [selectionCounts, setSelectionCounts] = useState({ columns: 1, rows: 1 })
  const [canMergeCells, setCanMergeCells] = useState(false)
  const [canUnmergeCell, setCanUnmergeCell] = useState(false)
  const [backgroundColor, setBackgroundColor] = useState(
    () => currentCellBackgroundColor(editor) || ''
  )

  // ✅ IMPORTANT: do NOT call node methods in render; keep headerState in React state
  const [headerState, setHeaderState] = useState<number>(0)

  const readMenuStateFromEditor = useCallback(
    (nextCell?: TableCellNode) => {
      editor.getEditorState().read(() => {
        const selection = $getSelection()

        // selection shape / merge availability
        if ($isTableSelection(selection)) {
          const currentSelectionCounts = computeSelectionCount(selection)
          setSelectionCounts(currentSelectionCounts)
          setCanMergeCells(
            isTableSelectionRectangular(selection) &&
            (currentSelectionCounts.columns > 1 ||
              currentSelectionCounts.rows > 1)
          )
        } else {
          setSelectionCounts({ columns: 1, rows: 1 })
          setCanMergeCells(false)
        }

        // unmerge availability
        setCanUnmergeCell($canUnmerge())

        // background color + header state (safe inside read)
        const cell = nextCell ?? tableCellNode
        const latest = cell.getLatest()
        setTableCellNode(latest)

        const bg = currentCellBackgroundColor(editor) || ''
        setBackgroundColor(bg)

        const hs =
          (latest as any).getHeaderState?.() ??
          (latest as any).getHeaderStyles?.() ??
          0
        setHeaderState(hs)
      })
    },
    [editor, tableCellNode]
  )

  // keep menu state in sync when the cell changes
  useEffect(() => {
    readMenuStateFromEditor(tableCellNode)
  }, [readMenuStateFromEditor, tableCellNode])

  // keep menu state in sync when the cell mutates
  useEffect(() => {
    const cellKey = tableCellNode.getKey()
    return editor.registerMutationListener(TableCellNode, (nodeMutations) => {
      const mutation = nodeMutations.get(cellKey)
      if ((mutation === 'updated' || mutation === 'created') && editor.getRootElement() !== null) {
        readMenuStateFromEditor(tableCellNode)
      }
    })
  }, [editor, tableCellNode, readMenuStateFromEditor])

  // position dropdown near trigger button
  useEffect(() => {
    const menuButtonElement = contextRef.current
    const dropDownElement = dropDownRef.current
    const rootElement = editor.getRootElement()

    if (!menuButtonElement || !dropDownElement || !rootElement) return

    const rootEleRect = rootElement.getBoundingClientRect()
    const menuButtonRect = menuButtonElement.getBoundingClientRect()
    dropDownElement.style.opacity = '1'

    const dropDownElementRect = dropDownElement.getBoundingClientRect()
    const margin = 5

    let leftPosition = menuButtonRect.right + margin
    if (
      leftPosition + dropDownElementRect.width > window.innerWidth ||
      leftPosition + dropDownElementRect.width > rootEleRect.right
    ) {
      const position = menuButtonRect.left - dropDownElementRect.width - margin
      leftPosition = (position < 0 ? margin : position) + window.scrollX
    }
    dropDownElement.style.left = `${leftPosition}px`

    let topPosition = menuButtonRect.top
    if (topPosition + dropDownElementRect.height > window.innerHeight) {
      const position = menuButtonRect.bottom - dropDownElementRect.height
      topPosition = (position < 0 ? margin : position) + window.scrollY
    }
    dropDownElement.style.top = `${topPosition + window.scrollY}px`
  }, [contextRef, editor])

  // close menu if clicking outside
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (
        dropDownRef.current &&
        contextRef.current &&
        !dropDownRef.current.contains(event.target as Node) &&
        !contextRef.current.contains(event.target as Node)
      ) {
        setIsMenuOpen(false)
      }
    }
    window.addEventListener('click', handleClickOutside)
    return () => window.removeEventListener('click', handleClickOutside)
  }, [setIsMenuOpen, contextRef])

  const clearTableSelection = useCallback(() => {
    editor.update(() => {
      if (tableCellNode.isAttached()) {
        const tableNode = $getTableNodeFromLexicalNodeOrThrow(tableCellNode)
        const tableElement = editor.getElementByKey(tableNode.getKey())
        if (!tableElement) throw new Error('Expected to find tableElement in DOM')

        const tableSelection = getTableObserverFromTableElement(tableElement as any)
        if (tableSelection !== null) {
          // compat
          if ((tableSelection as any).$clearHighlight) {
            ; (tableSelection as any).$clearHighlight()
          } else if ((tableSelection as any).clearHighlight) {
            ; (tableSelection as any).clearHighlight()
          }
        }

        tableNode.markDirty()
        setTableCellNode(tableCellNode.getLatest())
      }

      const rootNode = $getRoot()
      rootNode.selectStart()
    })
  }, [editor, tableCellNode])

  const mergeTableCellsAtSelection = useCallback(() => {
    editor.update(() => {
      const selection = $getSelection()
      if (!$isTableSelection(selection)) return

      const { columns, rows } = computeSelectionCount(selection)
      const nodes = selection.getNodes()
      let firstCell: TableCellNode | null = null

      for (let i = 0; i < nodes.length; i++) {
        const node = nodes[i]
        if ($isTableCellNode(node)) {
          if (firstCell === null) {
            node.setColSpan(columns).setRowSpan(rows)
            firstCell = node

            const isEmpty = $cellContainsEmptyParagraph(node)
            let firstChild
            if (isEmpty && $isParagraphNode((firstChild = node.getFirstChild()))) {
              firstChild.remove()
            }
          } else {
            const isEmpty = $cellContainsEmptyParagraph(node)
            if (!isEmpty) firstCell.append(...node.getChildren())
            node.remove()
          }
        }
      }

      if (firstCell !== null) {
        if (firstCell.getChildrenSize() === 0) {
          firstCell.append($createParagraphNode())
        }
        $selectLastDescendant(firstCell)
      }
    })

    onClose()
  }, [editor, onClose])

  const unmergeTableCellsAtSelection = useCallback(() => {
    editor.update(() => {
      $unmergeCell()
    })
    onClose()
  }, [editor, onClose])

  const insertTableRowAtSelection = useCallback(
    (shouldInsertAfter: boolean) => {
      editor.update(() => {
        $insertTableRow__EXPERIMENTAL(shouldInsertAfter)
      })
      onClose()
    },
    [editor, onClose]
  )

  const insertTableColumnAtSelection = useCallback(
    (shouldInsertAfter: boolean) => {
      editor.update(() => {
        for (let i = 0; i < selectionCounts.columns; i++) {
          $insertTableColumn__EXPERIMENTAL(shouldInsertAfter)
        }
      })
      onClose()
    },
    [editor, onClose, selectionCounts.columns]
  )

  const deleteTableRowAtSelection = useCallback(() => {
    editor.update(() => {
      $deleteTableRow__EXPERIMENTAL()
    })
    onClose()
  }, [editor, onClose])

  const deleteTableColumnAtSelection = useCallback(() => {
    editor.update(() => {
      $deleteTableColumn__EXPERIMENTAL()
    })
    onClose()
  }, [editor, onClose])

  const deleteTableAtSelection = useCallback(() => {
    editor.update(() => {
      const tableNode = $getTableNodeFromLexicalNodeOrThrow(tableCellNode)
      tableNode.remove()
    })

    clearTableSelection()
    onClose()
  }, [editor, tableCellNode, clearTableSelection, onClose])

  const toggleTableRowIsHeader = useCallback(() => {
    editor.update(() => {
      const tableNode = $getTableNodeFromLexicalNodeOrThrow(tableCellNode)
      const tableRowIndex = $getTableRowIndexFromTableCellNode(tableCellNode)
      const tableRows = tableNode.getChildren()

      if (tableRowIndex >= tableRows.length || tableRowIndex < 0) {
        throw new Error('Expected table cell to be inside of table row.')
      }

      const tableRow = tableRows[tableRowIndex]
      if (!$isTableRowNode(tableRow)) throw new Error('Expected table row')

      tableRow.getChildren().forEach((tableCell) => {
        if (!$isTableCellNode(tableCell)) throw new Error('Expected table cell')
        tableCell.toggleHeaderStyle(TableCellHeaderStates.ROW)
      })
    })

    clearTableSelection()
    onClose()
  }, [editor, tableCellNode, clearTableSelection, onClose])

  const toggleTableColumnIsHeader = useCallback(() => {
    editor.update(() => {
      const tableNode = $getTableNodeFromLexicalNodeOrThrow(tableCellNode)
      const tableColumnIndex = $getTableColumnIndexFromTableCellNode(tableCellNode)

      const tableRows = tableNode.getChildren()
      const maxRowsLength = Math.max(
        ...tableRows.map((row) => (row as TableRowNode).getChildren().length)
      )

      if (tableColumnIndex >= maxRowsLength || tableColumnIndex < 0) {
        throw new Error('Expected table cell to be inside of table row.')
      }

      for (let r = 0; r < tableRows.length; r++) {
        const tableRow = tableRows[r]
        if (!$isTableRowNode(tableRow)) throw new Error('Expected table row')

        const tableCells = tableRow.getChildren()
        if (tableColumnIndex >= tableCells.length) continue

        const tableCell = tableCells[tableColumnIndex]
        if (!$isTableCellNode(tableCell)) throw new Error('Expected table cell')

        tableCell.toggleHeaderStyle(TableCellHeaderStates.COLUMN)
      }
    })

    clearTableSelection()
    onClose()
  }, [editor, tableCellNode, clearTableSelection, onClose])

  const handleCellBackgroundColor = useCallback(
    (value: string) => {
      editor.update(() => {
        const selection = $getSelection()
        if (!($isRangeSelection(selection) || $isTableSelection(selection))) return

        const [cell] = $getNodeTriplet(selection.anchor)
        if ($isTableCellNode(cell)) {
          cell.setBackgroundColor(value)
        }

        if ($isTableSelection(selection)) {
          const nodes = selection.getNodes()
          for (let i = 0; i < nodes.length; i++) {
            const node = nodes[i]
            if ($isTableCellNode(node)) {
              node.setBackgroundColor(value)
            }
          }
        }
      })
    },
    [editor]
  )

const [pos, setPos] = useState({ top: 0, left: 0 });

const updatePosition = useCallback(() => {
  const anchor = contextRef.current;   // ✅ button
  const menu = dropDownRef.current;    // ✅ menu
  if (!anchor || !menu) return;

  const r = anchor.getBoundingClientRect();

  // default open below
  let top = r.bottom + 6;
  let left = r.left;

  // menu size
  const m = menu.getBoundingClientRect();

  // keep in viewport horizontally
  if (left + m.width > window.innerWidth - 8) {
    left = window.innerWidth - m.width - 8;
  }
  if (left < 8) left = 8;

  // keep in viewport vertically
  if (top + m.height > window.innerHeight - 8) {
    top = r.top - m.height - 6; // open above
  }
  if (top < 8) top = 8;

  setPos({ top, left });
}, [contextRef]);

React.useLayoutEffect(() => {
  updatePosition();
}, [updatePosition]);

useEffect(() => {
  window.addEventListener("scroll", updatePosition, true);
  window.addEventListener("resize", updatePosition);
  return () => {
    window.removeEventListener("scroll", updatePosition, true);
    window.removeEventListener("resize", updatePosition);
  };
}, [updatePosition]);



  let mergeCellButton: React.ReactNode = null
  if (cellMerge) {
    if (canMergeCells) {
      mergeCellButton = (
        <button
          type="button"
          className="item px-4 py-2 my-1 rounded hover:bg-accent hover:text-accent-foreground text-muted-foreground w-full text-left font-medium text-sm"
          onClick={mergeTableCellsAtSelection}
          data-test-id="table-merge-cells"
        >
          Merge cells
        </button>
      )
    } else if (canUnmergeCell) {
      mergeCellButton = (
        <button
          type="button"
          className="item px-4 py-2 my-1 rounded hover:bg-accent hover:text-accent-foreground text-muted-foreground w-full text-left font-medium text-sm"
          onClick={unmergeTableCellsAtSelection}
          data-test-id="table-unmerge-cells"
        >
          Unmerge cells
        </button>
      )
    }
  }

  return createPortal(
    <div
       style={{ position: "fixed", top: pos.top, left: pos.left }}
      className="absolute block bg-popover border border-border shadow-xl rounded-lg z-50 px-2 py-2 w-64 text-popover-foreground"
      ref={dropDownRef}
      onClick={(e) => e.stopPropagation()}
    >
      {mergeCellButton}

      <button
        type="button"
        className="px-4 py-2 my-1 rounded hover:bg-accent hover:text-accent-foreground text-muted-foreground w-full text-left font-medium text-sm"
        onClick={() =>
          showColorPickerModal('Cell background color', () => (
            <ColorPicker color={backgroundColor} onChange={handleCellBackgroundColor} />
          ))
        }
        data-test-id="table-background-color"
      >
        <span>Background color</span>
      </button>

      <hr className="my-1 border-border" />

      <button
        type="button"
        className="px-4 py-2 my-1 rounded hover:bg-accent hover:text-accent-foreground text-muted-foreground w-full text-left font-medium text-sm"
        onClick={() => insertTableRowAtSelection(false)}
        data-test-id="table-insert-row-above"
      >
        <span>
          Insert {selectionCounts.rows === 1 ? 'row' : `${selectionCounts.rows} rows`} above
        </span>
      </button>

      <button
        type="button"
        className="px-4 py-2 my-1 rounded hover:bg-accent hover:text-accent-foreground text-muted-foreground w-full text-left font-medium text-sm"
        onClick={() => insertTableRowAtSelection(true)}
        data-test-id="table-insert-row-below"
      >
        <span>
          Insert {selectionCounts.rows === 1 ? 'row' : `${selectionCounts.rows} rows`} below
        </span>
      </button>

      <hr className="my-1 border-border" />

      <button
        type="button"
        className="px-4 py-2 my-1 rounded hover:bg-accent hover:text-accent-foreground text-muted-foreground w-full text-left font-medium text-sm"
        onClick={() => insertTableColumnAtSelection(false)}
        data-test-id="table-insert-column-before"
      >
        <span>
          Insert {selectionCounts.columns === 1 ? 'column' : `${selectionCounts.columns} columns`} left
        </span>
      </button>

      <button
        type="button"
        className="px-4 py-2 my-1 rounded hover:bg-accent hover:text-accent-foreground text-muted-foreground w-full text-left font-medium text-sm"
        onClick={() => insertTableColumnAtSelection(true)}
        data-test-id="table-insert-column-after"
      >
        <span>
          Insert {selectionCounts.columns === 1 ? 'column' : `${selectionCounts.columns} columns`} right
        </span>
      </button>

      <hr className="my-1 border-border" />

      <button
        type="button"
        className="px-4 py-2 my-1 rounded hover:bg-accent hover:text-accent-foreground text-muted-foreground w-full text-left font-medium text-sm"
        onClick={deleteTableColumnAtSelection}
        data-test-id="table-delete-columns"
      >
        <span>Delete column</span>
      </button>

      <button
        type="button"
        className="px-4 py-2 my-1 rounded hover:bg-accent hover:text-accent-foreground text-muted-foreground w-full text-left font-medium text-sm"
        onClick={deleteTableRowAtSelection}
        data-test-id="table-delete-rows"
      >
        <span>Delete row</span>
      </button>

      <button
        type="button"
        className="px-4 py-2 my-1 rounded hover:bg-accent hover:text-accent-foreground text-muted-foreground w-full text-left font-medium text-sm"
        onClick={deleteTableAtSelection}
        data-test-id="table-delete"
      >
        <span>Delete table</span>
      </button>

      <hr className="my-1 border-border" />

      <button
        type="button"
        className="px-4 py-2 my-1 rounded hover:bg-accent hover:text-accent-foreground text-muted-foreground w-full text-left font-medium text-sm"
        onClick={toggleTableRowIsHeader}
      >
        <span>
          {(headerState & TableCellHeaderStates.ROW) === TableCellHeaderStates.ROW
            ? 'Remove'
            : 'Add'}{' '}
          row header
        </span>
      </button>

      <button
        type="button"
        className="px-4 py-2 my-1 rounded hover:bg-accent hover:text-accent-foreground text-muted-foreground w-full text-left font-medium text-sm"
        onClick={toggleTableColumnIsHeader}
        data-test-id="table-column-header"
      >
        <span>
          {(headerState & TableCellHeaderStates.COLUMN) === TableCellHeaderStates.COLUMN
            ? 'Remove'
            : 'Add'}{' '}
          column header
        </span>
      </button>
    </div>,
    document.body
  )
}

function TableCellActionMenuContainer({
  anchorElem,
  cellMerge,
}: {
  anchorElem: HTMLElement
  cellMerge: boolean
}): React.JSX.Element {
  const [editor] = useLexicalComposerContext()

  const menuButtonRef = useRef<HTMLDivElement>(null)
  const menuRootRef = useRef<HTMLButtonElement>(null)
  const [isMenuOpen, setIsMenuOpen] = useState(false)
  const [tableCellNode, setTableMenuCellNode] = useState<TableCellNode | null>(null)

  const [colorPickerModal, showColorPickerModal] = useModal()

  // ✅ IMPORTANT: moveMenu must run inside editorState.read
  const moveMenu = useCallback(() => {
    const selection = $getSelection()
    const nativeSelection = window.getSelection()
    const activeElement = document.activeElement

    if (selection == null) {
      setTableMenuCellNode(null)
      return
    }

    const rootElement = editor.getRootElement()

    if (
      $isRangeSelection(selection) &&
      rootElement !== null &&
      nativeSelection !== null &&
      rootElement.contains(nativeSelection.anchorNode)
    ) {
      const tableCellNodeFromSelection = $getTableCellNodeFromLexicalNode(
        selection.anchor.getNode()
      )

      if (tableCellNodeFromSelection == null) {
        setTableMenuCellNode(null)
        return
      }

      const tableCellParentNodeDOM = editor.getElementByKey(
        tableCellNodeFromSelection.getKey()
      )
      if (tableCellParentNodeDOM == null) {
        setTableMenuCellNode(null)
        return
      }

      setTableMenuCellNode(tableCellNodeFromSelection)
    } else if (!activeElement) {
      setTableMenuCellNode(null)
    }
  }, [editor])

  useEffect(() => {
    return editor.registerUpdateListener(({ editorState }) => {
      if (editor.getRootElement() !== null) {
        editorState.read(() => {
          moveMenu()
        })
      }
    })
  }, [editor, moveMenu])

  useEffect(() => {
    const menuButtonDOM = menuButtonRef.current
    if (!menuButtonDOM) return

    if (tableCellNode != null) {
      const tableCellNodeDOM = editor.getElementByKey(tableCellNode.getKey())
      if (tableCellNodeDOM != null) {
        const tableCellRect = tableCellNodeDOM.getBoundingClientRect()
        const menuRect = menuButtonDOM.getBoundingClientRect()
        const anchorRect = anchorElem.getBoundingClientRect()

        const top = tableCellRect.top - anchorRect.top + 4
        const left = tableCellRect.right - menuRect.width - 10 - anchorRect.left

        menuButtonDOM.style.opacity = '1'
        menuButtonDOM.style.transform = `translate(${left}px, ${top}px)`
      } else {
        menuButtonDOM.style.opacity = '0'
        menuButtonDOM.style.transform = 'translate(-10000px, -10000px)'
      }
    } else {
      menuButtonDOM.style.opacity = '0'
      menuButtonDOM.style.transform = 'translate(-10000px, -10000px)'
    }
  }, [tableCellNode, editor, anchorElem])

  const prevTableCellDOM = useRef(tableCellNode)
  useEffect(() => {
    if (prevTableCellDOM.current !== tableCellNode) setIsMenuOpen(false)
    prevTableCellDOM.current = tableCellNode
  }, [tableCellNode])

  return (
    <div
      className="table-cell-action-button-container"
      style={{
        position: 'absolute',
        top: 0,
        left: 0,
        opacity: 0,
        transition: 'opacity 0.2s',
        willChange: 'transform',
        zIndex: 10000,
      }}
      ref={menuButtonRef}
    >
      {tableCellNode != null && (
        <>
          <button
            type="button"
            className="flex items-center justify-center w-5 h-5 bg-popover border border-border rounded cursor-pointer hover:bg-accent hover:text-accent-foreground transition-colors"
            onClick={(e) => {
              e.stopPropagation()
              setIsMenuOpen((v) => !v)
            }}
            ref={menuRootRef}
          >
            <ChevronDown className="h-3 w-3 text-muted-foreground" />
          </button>

          {colorPickerModal}

          {isMenuOpen && (
            <TableActionMenu
              contextRef={menuRootRef}
              setIsMenuOpen={setIsMenuOpen}
              onClose={() => setIsMenuOpen(false)}
              tableCellNode={tableCellNode}
              cellMerge={cellMerge}
              showColorPickerModal={showColorPickerModal}
            />
          )}
        </>
      )}
    </div>
  )
}

export default function TableActionMenuPlugin({
  anchorElem = document.body,
  cellMerge = false,
}: {
  anchorElem?: HTMLElement
  cellMerge?: boolean
}): React.ReactPortal | null {
  const isEditable = useLexicalEditable()
  return isEditable
    ? createPortal(
      <TableCellActionMenuContainer anchorElem={anchorElem} cellMerge={cellMerge} />,
      anchorElem
    )
    : null
}
